import React, { useState, useEffect } from 'react';
import { accountingAPI, formatCurrency } from '../services/api';
import { toast } from 'sonner';
import { Download, CheckCircle, XCircle, ChevronDown, ChevronRight, X, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#ec4899'];

// Account codes whose row is clickable for a party-wise drill-down.
// 11004 = Accounts Receivable (Debtors), 21001 = Accounts Payable (Creditors).
// Easy to extend in future for any control account.
const PARTY_DRILLDOWN_ACCOUNTS = ['11004', '21001'];

export default function BalanceSheet() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [asOf, setAsOf] = useState('');
  const [expanded, setExpanded] = useState({ assets: true, liabilities: true, equity: true });
  const [drill, setDrill] = useState(null);   // { code, name }
  const [drillData, setDrillData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (asOf) params.as_of = asOf;
      const res = await accountingAPI.balanceSheet(params);
      setData(res.data);
    } catch { toast.error('Failed to load balance sheet'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [asOf]);

  const openDrill = async (item) => {
    setDrill({ code: item.account_code, name: item.account_name });
    setDrillData(null);
    try {
      const r = await accountingAPI.accountLedgerByParty(item.account_code);
      setDrillData(r.data);
    } catch { toast.error('Could not load party-wise breakdown'); setDrill(null); }
  };

  const handleExportCSV = () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const params = new URLSearchParams({ format: 'csv' });
    if (asOf) params.set('as_of', asOf);
    window.open(`${API_URL}/api/accounting/balance-sheet/export?${params.toString()}`, '_blank');
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;

  const pieData = [
    { name: 'Assets', value: data?.assets?.total || 0 },
    { name: 'Liabilities', value: data?.liabilities?.total || 0 },
    { name: 'Equity', value: data?.equity?.total || 0 },
  ].filter(d => d.value > 0);

  const toggle = (section) => setExpanded(e => ({ ...e, [section]: !e[section] }));

  const renderSection = (title, items, total, key, color) => (
    <div className="mb-1">
      <button onClick={() => toggle(key)} className={`w-full flex items-center justify-between px-5 py-3 ${color} hover:opacity-90 transition-opacity`}>
        <div className="flex items-center gap-2">
          {expanded[key] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span className="font-semibold text-sm">{title}</span>
        </div>
        <span className="font-bold font-mono text-sm">Rs. {formatCurrency(total)}</span>
      </button>
      {expanded[key] && items?.map((item) => {
        const isDrillable = PARTY_DRILLDOWN_ACCOUNTS.includes(item.account_code);
        return (
          <div
            key={item.account_code}
            onClick={isDrillable ? () => openDrill(item) : undefined}
            className={`flex items-center justify-between px-5 py-2.5 pl-12 border-b border-slate-100 ${isDrillable ? 'cursor-pointer hover:bg-blue-50' : 'hover:bg-slate-50'}`}
            data-testid={`bs-row-${item.account_code}`}
            title={isDrillable ? 'Click for party-wise breakdown' : undefined}
          >
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-slate-500 mr-2">{item.account_code}</span>
              <span className={`text-sm ${isDrillable ? 'text-blue-700 font-medium hover:underline' : 'text-slate-700'}`}>{item.account_name}</span>
              {isDrillable && <Users className="w-3 h-3 text-blue-500 opacity-70" />}
            </div>
            <span className="font-mono text-sm">{formatCurrency(item.amount)}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div data-testid="balance-sheet-page" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Balance Sheet</h1>
          <p className="text-sm text-slate-500 mt-1">As of: {data?.as_of || 'Current'} · Click <span className="text-blue-600 font-medium">Accounts Receivable</span> or <span className="text-blue-600 font-medium">Accounts Payable</span> for party-wise breakdown.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="form-input w-36" data-testid="bs-date" />
          {data && (
            data.is_balanced ? (
              <span className="flex items-center gap-1 text-xs text-emerald-600 font-semibold bg-emerald-50 px-3 py-1.5 rounded-full" data-testid="bs-balanced">
                <CheckCircle className="w-4 h-4" /> Balanced
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-rose-600 font-semibold bg-rose-50 px-3 py-1.5 rounded-full" data-testid="bs-unbalanced">
                <XCircle className="w-4 h-4" /> Unbalanced
              </span>
            )
          )}
          <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="bs-export-csv">
            <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="bs-assets-total">
          <p className="text-[10px] font-semibold text-blue-600 uppercase">Total Assets</p>
          <p className="text-2xl font-bold font-mono text-blue-700 mt-1">Rs. {formatCurrency(data?.assets?.total || 0)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="bs-liabilities-total">
          <p className="text-[10px] font-semibold text-rose-600 uppercase">Total Liabilities</p>
          <p className="text-2xl font-bold font-mono text-rose-700 mt-1">Rs. {formatCurrency(data?.liabilities?.total || 0)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="bs-equity-total">
          <p className="text-[10px] font-semibold text-purple-600 uppercase">Total Equity</p>
          <p className="text-2xl font-bold font-mono text-purple-700 mt-1">Rs. {formatCurrency(data?.equity?.total || 0)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="bs-detail">
          {renderSection('Assets', data?.assets?.items, data?.assets?.total || 0, 'assets', 'bg-blue-50 text-blue-800')}
          {renderSection('Liabilities', data?.liabilities?.items, data?.liabilities?.total || 0, 'liabilities', 'bg-rose-50 text-rose-800')}
          {renderSection('Equity', data?.equity?.items, data?.equity?.total || 0, 'equity', 'bg-purple-50 text-purple-800')}
          <div className="flex items-center justify-between px-5 py-4 bg-slate-900 text-white">
            <span className="font-bold text-sm">Total Liabilities + Equity</span>
            <span className="font-bold font-mono text-sm" data-testid="bs-total-le">Rs. {formatCurrency(data?.total_liabilities_and_equity || 0)}</span>
          </div>
        </div>
        {pieData.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <h3 className="font-heading font-semibold text-slate-800 mb-4 text-sm">Composition</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pieData.map((entry) => <Cell key={entry.name} fill={COLORS[pieData.indexOf(entry) % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `Rs. ${formatCurrency(v)}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Party-wise drill-down modal */}
      {drill && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-3" data-testid="ar-ap-drill-modal">
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-auto">
            <div className="border-b border-slate-200 dark:border-slate-700 p-4 flex items-center justify-between sticky top-0 bg-white dark:bg-slate-900 z-10">
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  {drill.name} — Party-wise Breakdown
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">Account code: <span className="font-mono">{drill.code}</span></p>
              </div>
              <button onClick={() => setDrill(null)} className="text-slate-400 hover:text-slate-600" data-testid="drill-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              {!drillData ? <p className="text-sm text-slate-500">Loading…</p> : (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-blue-50 rounded p-3"><div className="text-[10px] uppercase font-semibold text-blue-700">Total Debit</div><div className="text-lg font-mono font-bold text-blue-900">Rs. {formatCurrency(drillData.total_debit)}</div></div>
                    <div className="bg-rose-50 rounded p-3"><div className="text-[10px] uppercase font-semibold text-rose-700">Total Credit</div><div className="text-lg font-mono font-bold text-rose-900">Rs. {formatCurrency(drillData.total_credit)}</div></div>
                    <div className="bg-emerald-50 rounded p-3"><div className="text-[10px] uppercase font-semibold text-emerald-700">Net Balance</div><div className="text-lg font-mono font-bold text-emerald-900">Rs. {formatCurrency(drillData.net_balance)}</div></div>
                  </div>
                  <div className="text-xs text-slate-500 mb-2">{drillData.party_count} parties · sorted by balance</div>
                  <table className="w-full text-sm" data-testid="drill-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left p-2 text-[10px] font-semibold text-slate-500 uppercase">Party</th>
                        <th className="text-right p-2 text-[10px] font-semibold text-slate-500 uppercase">Debit</th>
                        <th className="text-right p-2 text-[10px] font-semibold text-slate-500 uppercase">Credit</th>
                        <th className="text-right p-2 text-[10px] font-semibold text-slate-500 uppercase">Balance</th>
                        <th className="text-right p-2 text-[10px] font-semibold text-slate-500 uppercase">Txns</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {drillData.parties.map((p) => (
                        <tr key={p.party_name} className="hover:bg-slate-50">
                          <td className="p-2 font-medium">{p.party_name}</td>
                          <td className="p-2 text-right font-mono text-slate-600">{p.debit ? `Rs.${formatCurrency(p.debit)}` : '-'}</td>
                          <td className="p-2 text-right font-mono text-slate-600">{p.credit ? `Rs.${formatCurrency(p.credit)}` : '-'}</td>
                          <td className={`p-2 text-right font-mono font-semibold ${p.balance >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>Rs.{formatCurrency(p.balance)}</td>
                          <td className="p-2 text-right text-slate-500 text-xs">{p.txn_count}</td>
                        </tr>
                      ))}
                      {drillData.parties.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-6">No parties with balances against this account.</td></tr>}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
